import React, { useEffect, useRef } from 'react';
import styles from 'src/new/components/TeamDetail/TeamDetail.module.scss';
import { useDistance } from 'new/hooks/useDistance';
import { Subheader } from 'new/elements/Subheader';
import { Table } from 'new/elements/Table';
import { TableProfile } from 'new/elements/TableProfile';
import { columns } from 'new/components/TeamDetail/columns';
import { Button, IconButton, InputAdornment, Tooltip, Typography } from '@mui/material';
import { ChipBadge } from 'new/elements/ChipBadge';
import { TextField } from 'new/elements/TextField';
import { DeleteOutlineRounded, EditRounded, Search as SearchIcon } from '@mui/icons-material';
import { ScreenComponent } from 'new/routes/RouterManager';
import { ROUTE_REDESIGN } from 'js/constants/pathnames';
import { ROUTE_COLLABORATE_TEAM_ID } from 'new/constants/pathnames';
import { useTeam } from 'new/hooks/adapters/useTeam';
import { useAppContext } from 'new/hooks/useAppContext';
import { AddTeam } from 'new/components/settings/Teams/AddTeam';
import { useModals } from 'new/hooks/useModals';
import { useSelector } from 'react-redux';
import { fetchOrgMembers, updateTeamInfo } from 'js/actions';
import { useIsLoading } from 'new/hooks/adapters/useIsLoading';
import {
  GET_TEAMS_REQUEST,
  UPDATE_TEAM_INFO_REQUEST,
  UPDATE_TEAM_MEMBERS_REQUEST,
} from 'js/constants/actionTypes';
import { useCurrentOrg } from 'new/hooks/adapters/useCurrentOrg';
import { useFilteredData } from 'new/hooks/useFilteredData';
import useAppDispatch from 'js/hooks/useAppDispatch';

interface TeamDetailProps {
  match: {
    params: {
      id: number;
    };
  };
}

function Search({ onSearchChange }) {
  return (
    <TextField
      label=""
      onChange={onSearchChange}
      placeholder="Search users"
      InputProps={{
        startAdornment: (
          <InputAdornment position="start">
            <SearchIcon />
          </InputAdornment>
        ),
      }}
    />
  );
}

export const TeamDetail = (props: TeamDetailProps) => {
  const {
    match: {
      params: { id },
    },
  } = props;
  const { setCurrentSlug } = useAppContext();
  const { openModal } = useModals();
  const org = useCurrentOrg();
  const { orgMembers } = useSelector((state: any) => state.orgs);
  const dispatch = useAppDispatch();
  const loading = useIsLoading(
    GET_TEAMS_REQUEST,
    UPDATE_TEAM_INFO_REQUEST,
    UPDATE_TEAM_MEMBERS_REQUEST,
  );

  const ref = useRef();
  const tableHeight = useDistance(ref, 400);
  const team = useTeam(id);
  const { onSearchChange, filteredData: members } = useFilteredData(
    team?.members || [],
    'full_name',
  );

  const onClickEdit = () => {
    openModal(AddTeam.modalConfig.name, { team });
  };

  useEffect(() => {
    if (!orgMembers[org.id]?.lastFetched) {
      dispatch(fetchOrgMembers(org.id));
    }
  }, []);

  useEffect(() => {
    if (team?.name) {
      setCurrentSlug(team.name);
    }
  }, [team]);

  const onClickDeleteMember = (member) => () => {
    dispatch(
      updateTeamInfo(team.id, {
        name: team.name,
        description: team.description,
        members: team.members.filter((m) => m.id !== member.id).map((m) => ({ email: m.email })),
      }),
    );
  };

  if (!team) {
    return null;
  }

  return (
    <div>
      <div className={styles.header}>
        <Subheader title={team.name} />
      </div>
      <div ref={ref} className={styles.contentTop}>
        <div className={styles.title}>
          <Typography variant="subtitle1">Members</Typography>
          <ChipBadge label={team?.members?.length + ' members'} color="disabled" />
        </div>
        <div className={styles.actions}>
          <Search onSearchChange={onSearchChange} />
          <Button
            onClick={onClickEdit}
            color="primary"
            variant="contained"
            startIcon={<EditRounded />}
          >
            Edit Team
          </Button>
        </div>
      </div>
      <Table
        height={tableHeight}
        loading={loading}
        columns={columns}
        rows={members}
        rowEndRender={(member) => (
          <Tooltip title={`Remove ${member?.full_name || member?.email} from team`}>
            <IconButton
              sx={{
                color: 'text.secondary',
                ':hover': {
                  color: 'error.main',
                },
              }}
              onClick={onClickDeleteMember(member)}
            >
              <DeleteOutlineRounded />
            </IconButton>
          </Tooltip>
        )}
      />
    </div>
  );
};

export const TeamDetailCollaborate: ScreenComponent<TeamDetailProps> = (props: TeamDetailProps) => (
  <TeamDetail {...props} />
);
export const TeamDetailSettings: ScreenComponent<TeamDetailProps> = (props: TeamDetailProps) => (
  <TeamDetail {...props} />
);

TeamDetailCollaborate.screenConfig = {
  domain: 'collaborate',
  modals: [AddTeam],
  pageTitle: 'Team Detail',
  header: {
    title: 'Collaborate',
  },
  path: `${ROUTE_REDESIGN}${ROUTE_COLLABORATE_TEAM_ID}`,
  screenName: 'TeamDetail',
};

TeamDetailSettings.screenConfig = {
  domain: 'settings',
  modals: [AddTeam],
  pageTitle: 'Team Detail',
  header: {
    title: 'Settings',
  },
  path: `${ROUTE_REDESIGN}/settings/team/:id`,
  screenName: 'TeamDetail',
};
